// Arşiv detay (readonly) — plan §4.6.2 + §4.6.3 (document versiyonları).

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { formatReportingPeriod, type ArchiveDetail, type DocumentInfo } from "@mahir/shared";
import { api, ApiError } from "../api/client";
import { documentTypeLabel, isDraftPreview } from "../lib/document-label";

const TYPE_LABELS: Record<string, { label: string; color: string }> = {
  full: { label: "Tam Rapor", color: "var(--color-success)" },
  partial: { label: "Kısmi", color: "var(--color-primary)" },
  draft: { label: "Taslak", color: "var(--color-text-3)" },
};

export function ArchiveDetailPage(): JSX.Element {
  const { id = "" } = useParams();
  const [detail, setDetail] = useState<ArchiveDetail | null>(null);
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let aborted = false;
    setLoading(true);
    Promise.all([
      api<ArchiveDetail>(`/archive/${id}`),
      api<{ documents: DocumentInfo[] }>(`/archive/${id}/documents`).catch(() => ({
        documents: [] as DocumentInfo[],
      })),
    ])
      .then(([d, docs]) => {
        if (aborted) return;
        setDetail(d);
        setDocuments(docs.documents);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!aborted) {
          setError(
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Yüklenemedi",
          );
        }
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [id]);

  const downloadDoc = async (docId: string): Promise<void> => {
    try {
      const res = await api<{ downloadUrl: string }>(`/projects/${id}/documents/${docId}`);
      window.open(res.downloadUrl, "_blank");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "İndirilemedi");
    }
  };

  if (loading) {
    return (
      <div className="p-8" style={{ color: "var(--color-text-2)" }}>
        Yükleniyor…
      </div>
    );
  }
  if (error || !detail) {
    return (
      <div className="p-8">
        <div
          className="max-w-2xl px-4 py-3 rounded-lg text-sm"
          style={{
            background: "color-mix(in srgb, var(--color-error) 10%, white)",
            color: "var(--color-error)",
          }}
        >
          {error ?? "Arşivde bulunamadı"}
        </div>
        <Link
          to="/archive"
          className="inline-block mt-4 text-sm"
          style={{ color: "var(--color-primary)" }}
        >
          ← Arşive dön
        </Link>
      </div>
    );
  }

  return (
    <div className="p-8 mx-auto" style={{ fontFamily: "var(--font-sans)", maxWidth: 1100 }}>
      <Link to="/archive" className="text-sm" style={{ color: "var(--color-primary)" }}>
        ← Arşive dön
      </Link>

      <div
        className="mt-4 mb-6 rounded-xl p-6"
        style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
      >
        <div className="flex items-center gap-2 mb-2">
          <span
            style={{
              padding: "2px 10px",
              borderRadius: 999,
              background: "color-mix(in srgb, var(--color-success) 10%, white)",
              color: "var(--color-success)",
              fontSize: "0.72rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Tamamlandı
          </span>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "0.75rem",
              color: "var(--color-text-3)",
            }}
          >
            {detail.frameworkVersion} ·{" "}
            {formatReportingPeriod(detail.reportingYear, detail.periodType)}
          </span>
        </div>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.8rem",
            color: "var(--color-text)",
          }}
        >
          {detail.clientName}
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--color-text-2)" }}>
          {detail.frameworkName} · {formatReportingPeriod(detail.reportingYear, detail.periodType)}
          {detail.clientSector && ` · ${detail.clientSector}`}
          {detail.clientSize && ` · ${detail.clientSize}`}
        </p>
        <div className="mt-3 text-sm flex gap-3 flex-wrap" style={{ color: "var(--color-text-3)" }}>
          <span>
            Danışman:{" "}
            <strong style={{ color: "var(--color-text-2)" }}>{detail.assignedToName}</strong>
          </span>
          <span>·</span>
          <span>
            Oluşturulma:{" "}
            {new Date(detail.createdAt).toLocaleDateString("tr-TR", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
          <span>·</span>
          <span>
            Tamamlandı:{" "}
            {new Date(detail.completedAt).toLocaleDateString("tr-TR", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
        </div>
      </div>

      {/* Döküman versiyonları */}
      <div
        className="rounded-xl p-6 mb-6"
        style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
      >
        <h2
          className="mb-3"
          style={{
            fontFamily: "var(--font-display)",
            color: "var(--color-text)",
            fontSize: "1.2rem",
          }}
        >
          Döküman Versiyonları
        </h2>
        {documents.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--color-text-3)" }}>
            Bu projede döküman kaydı yok.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {documents.map((d) => {
              const typeInfo = TYPE_LABELS[d.type] ?? TYPE_LABELS.draft!;
              return (
                <li
                  key={d.id}
                  className="flex items-center justify-between p-3 rounded border"
                  style={{
                    borderColor: "var(--color-border)",
                    opacity: d.supersededAt ? 0.6 : 1,
                  }}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className="px-2 py-0.5 rounded text-xs"
                        style={{
                          background: "var(--color-bg)",
                          color: typeInfo.color,
                          fontWeight: 600,
                        }}
                      >
                        {isDraftPreview(d) ? documentTypeLabel(d) : typeInfo.label}
                      </span>
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: "0.78rem",
                          color: "var(--color-text-3)",
                        }}
                      >
                        v{d.version} · {(d.fileSizeBytes / 1024).toFixed(1)} KB
                      </span>
                      {d.supersededAt && (
                        <span
                          className="text-xxs uppercase tracking-wider"
                          style={{
                            color: "var(--color-text-3)",
                            fontSize: "0.65rem",
                          }}
                        >
                          eski sürüm
                        </span>
                      )}
                    </div>
                    <div className="text-xs mt-1" style={{ color: "var(--color-text-3)" }}>
                      {new Date(d.generatedAt).toLocaleDateString("tr-TR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                  <button
                    onClick={() => void downloadDoc(d.id)}
                    className="px-3 py-1.5 rounded text-xs"
                    style={{
                      background: "var(--color-primary)",
                      color: "var(--color-surface)",
                      fontWeight: 600,
                    }}
                  >
                    İndir
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Readonly snapshot info */}
      {detail.snapshot && (
        <div
          className="rounded-xl p-6"
          style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
        >
          <h2
            className="mb-3"
            style={{
              fontFamily: "var(--font-display)",
              color: "var(--color-text)",
              fontSize: "1.2rem",
            }}
          >
            Çerçeve Kopyası
          </h2>
          <p className="text-sm" style={{ color: "var(--color-text-2)" }}>
            Proje oluşturulduğu andaki çerçeve hali değişmez olarak saklı. Bölüm + soru sayıları:
          </p>
          <p
            className="mt-2 text-sm"
            style={{ color: "var(--color-text-3)", fontFamily: "var(--font-mono)" }}
          >
            {(detail.snapshot.sections as unknown[]).length} bölüm ·{" "}
            {(detail.snapshot.questions as unknown[]).length} soru
          </p>
        </div>
      )}
    </div>
  );
}
